// src/InsightCategoryPage.js
import React, { useContext, useMemo, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, Link, Collapse, IconButton, Stack, Divider
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon   from '@mui/icons-material/KeyboardArrowUp';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import { ThemeProvider } from '@mui/material/styles';
import { useParams } from 'react-router-dom';
import theme from '../../theme';
import NavBar from '../../components/NavBar';
import FilterBar from '../../components/FilterBar';
import { DataAndFilterContext } from '../../context/DataAndFilterContext';
import CustomerModal from '../../components/CustomerModal';

const TYPE_KEY = (s) => {
  const t = String(s || '').toLowerCase();
  if (t.includes('wish')) return 'Wish List';
  if (t.includes('neg'))  return 'Negative';
  return 'Positive';
};
const colorForType = (t) => (t === 'Positive' ? 'success' : (t === 'Wish List' ? 'info' : 'error'));

export default function InsightCategoryPage() {
  const { category: paramCategory } = useParams();
  const categoryParam = decodeURIComponent(paramCategory || '');
  const { filteredData } = useContext(DataAndFilterContext);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [openRow, setOpenRow] = useState(null);

  const { rows, summary } = useMemo(() => {
    const rows = [];
    const scores = [];
    const typeCounts = { 'Positive':0, 'Negative':0, 'Wish List':0 };

    for (const file of filteredData || []) {
      const push = (insight, fromJob) => {
        if (!insight) return;
        const cat = (insight.insight_category || '').trim();
        if (!cat || cat.toLowerCase() !== categoryParam.toLowerCase()) return;

        const score   = Number(insight.insight_sentiment_score);
        const type    = TYPE_KEY(insight.insight_sentiment_overall);
        const quote   = insight.insight_quote;
        const ts      = Array.isArray(insight.insight_timestamps)
          ? insight.insight_timestamps[0]
          : (insight.insight_timestamp || insight.timestamp || '');

        if (Number.isFinite(score)) scores.push(score);
        typeCounts[type] = (typeCounts[type] || 0) + 1;

        rows.push({
          file,
          customer_name: file.customer_name || '',
          conversation_date: file.conversation_date || '',
          insight: insight.insight || '',
          insight_summary: insight.insight_summary || '',
          insight_type: insight.insight_type || '',
          insight_category: insight.insight_category || '',
          insight_sentiment_overall: insight.insight_sentiment_overall || '',
          insight_sentiment_expressed: insight.insight_sentiment_expressed || '',
          insight_sentiment_score: score,
          insight_sentiment_justification: insight.insight_sentiment_justification || '',
          insight_quote: quote || '',
          insight_timestamp: ts || '',
          fromJob
        });
      };

      (file.jobs_to_be_done || []).forEach(j => (j.insights || []).forEach(i => push(i, true)));
      (file.miscellaneous_insights || []).forEach(i => push(i, false));
    }

    // sort by most recent then highest score as a tie-break
    rows.sort((a,b) => {
      const d = new Date(b.conversation_date) - new Date(a.conversation_date);
      if (d !== 0) return d;
      return (b.insight_sentiment_score || 0) - (a.insight_sentiment_score || 0);
    });

    const avgScore = scores.length ? (scores.reduce((x,y)=>x+y,0)/scores.length) : 0;
    const type = Object.entries(typeCounts).sort((a,b)=>b[1]-a[1])[0]?.[0] || 'Positive';

    return {
      rows,
      summary: { count: rows.length, avgScore, type }
    };
  }, [filteredData, categoryParam]);

  const onRowClick = (idx, row) => {
    setOpenRow(openRow === idx ? null : idx);
  };
  const openCustomerModal = (row) => {
    setSelectedCustomer(row.file);
    setModalOpen(true);
  };

  return (
    <ThemeProvider theme={theme}>
      <NavBar />
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Insight category: {categoryParam}
        </Typography>
        <FilterBar />

        <Box sx={{ mt: 2, mb: 2 }}>
          <Chip sx={{ mr: 1 }} label={`Count: ${summary.count}`} />
          <Chip sx={{ mr: 1 }} label={`Type: ${summary.type}`} color={colorForType(summary.type)} />
          <Chip label={`Avg sentiment score: ${summary.avgScore.toFixed(1)}`} />
        </Box>

        <Paper>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Customer</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Insight</TableCell>
                <TableCell sx={{
                  maxWidth: 500,
                  width: '50%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  Insight Summary
                </TableCell>
                <TableCell>Sentiment score (0–10)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r, idx) => (
                <React.Fragment key={idx}>
                  <TableRow hover sx={{ cursor:'pointer' }}>
                    <TableCell onClick={() => onRowClick(idx, r)} width={48}>
                      <IconButton size="small">
                        {openRow === idx ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell onClick={() => openCustomerModal(r)} sx={{ textDecoration:'underline' }}>
                      {r.customer_name}
                    </TableCell>
                    <TableCell onClick={() => onRowClick(idx, r)}>{r.conversation_date}</TableCell>
                    <TableCell onClick={() => onRowClick(idx, r)}>{r.insight}</TableCell>
                    <TableCell onClick={() => onRowClick(idx, r)} sx={{
                      maxWidth: 500,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}>
                      {r.insight_summary}
                    </TableCell>
                    <TableCell onClick={() => onRowClick(idx, r)}>
                      {Number.isFinite(r.insight_sentiment_score) ? r.insight_sentiment_score.toFixed(1) : '—'}
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell colSpan={6} sx={{ py: 0 }}>
                      <Collapse in={openRow === idx} timeout="auto" unmountOnExit>
                        <Box sx={{ m: 2 }}>
                          <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
                            <Chip label={`Type: ${TYPE_KEY(r.insight_sentiment_overall)}`} color={colorForType(TYPE_KEY(r.insight_sentiment_overall))} />
                            <Chip label={`Expressed: ${r.insight_sentiment_expressed || '—'}`} variant="outlined" />
                            <Chip label={`Score: ${Number.isFinite(r.insight_sentiment_score) ? r.insight_sentiment_score.toFixed(1) : '—'}`} />
                          </Stack>

                          <Divider sx={{ my: 1.5 }} />

                          <GridLike label="insight" value={r.insight} />
                          <GridLike label="insight_summary" value={r.insight_summary} />
                          <GridLike label="insight_type" value={r.insight_type} />
                          <GridLike label="insight_category" value={r.insight_category} />
                          <GridLike label="insight_sentiment_overall" value={r.insight_sentiment_overall} />
                          <GridLike label="insight_sentiment_expressed" value={r.insight_sentiment_expressed} />
                          <GridLike label="insight_sentiment_justification" value={r.insight_sentiment_justification} />
                          <GridLike
                            label="insight_quote"
                            value={
                              r.insight_quote ? (
                                <Link href="#" underline="hover">
                                  <OndemandVideoIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                                  “{r.insight_quote}”
                                </Link>
                              ) : '—'
                            }
                          />
                          <GridLike label="timestamp" value={r.insight_timestamp || '—'} />

                          <Box sx={{ mt: 2 }}>
                            <Typography
                              variant="body2"
                              sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                              onClick={() => openCustomerModal(r)}
                            >
                              Open customer conversation
                            </Typography>
                          </Box>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </Paper>

        <CustomerModal open={modalOpen} onClose={() => setModalOpen(false)} customer={selectedCustomer} />
      </Box>
    </ThemeProvider>
  );
}

// Small helper to render label/value neatly
function GridLike({ label, value }) {
  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, mr: 1, opacity: 0.7 }}>
        {label}:
      </Typography>
      <Typography variant="body2" component="span">{value || '—'}</Typography>
    </Box>
  );
}
